import React, { useEffect, useMemo, useState } from "react";

const MODEL_OPTIONS = [
  {
    value: "gemini",
    name: "Gemini 3.7 Flash",
    desc: "권장 기본값 — 빠른 텍스트·문서 처리와 멀티모달 지원.",
  },
  {
    value: "claude-sonnet",
    name: "Claude Sonnet 4.6",
    desc: "속도와 추론 품질의 균형이 필요할 때 선택.",
  },
  {
    value: "claude-opus",
    name: "Claude Opus 5",
    desc: "가장 정확한 검토용. 사고(thinking)가 기본으로 켜져 응답 시간과 비용이 가장 큼.",
  },
  {
    value: "openai",
    name: "GPT 5.6 Luna",
    desc: "OpenAI 모델을 별도 비교하거나 특정 역할에 적용할 때 선택.",
  },
];

const ROLE_INFO = [
  {
    key: "classification",
    name: "후보 생성·AI 검토",
    desc: "상품명 확장, 분류 후보 제안, AI 검토와 의견서 초안",
    allowDual: true,
  },
  {
    key: "factExtraction",
    name: "자연어·문서 사실추출",
    desc: "문장을 재질·구성·용도·형태·작동방식 등의 사실로 구조화",
  },
  {
    key: "answer",
    name: "AI 답변 작성",
    desc: "결정트리와 근거가 만든 결과를 사용자용 설명으로 작성",
  },
];

const INITIAL_SETTINGS = {
  defaultModel: "gemini",
  roles: {
    classification: "inherit",
    factExtraction: "inherit",
    answer: "inherit",
  },
};

const modelName = (value) => {
  if (value === "dual") return "Gemini + Claude 교차검증";
  return MODEL_OPTIONS.find((option) => option.value === value)?.name || value;
};

const normalizeResponse = (data) => ({
  defaultModel: data.defaultModel || "gemini",
  roles: {
    classification: data.roles?.classification?.setting || "inherit",
    factExtraction: data.roles?.factExtraction?.setting || "inherit",
    answer: data.roles?.answer?.setting || "inherit",
  },
});

const AdminLlmModel = ({ API_BASE_URL }) => {
  const [settings, setSettings] = useState(INITIAL_SETTINGS);
  const [saved, setSaved] = useState(INITIAL_SETTINGS);
  const [legacySettings, setLegacySettings] = useState([]);
  const [advanced, setAdvanced] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState("");

  const authHeaders = () => {
    const token = localStorage.getItem("admin_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/admin/llm-settings`, { headers: authHeaders() })
      .then((response) => {
        if (response.status === 401 || response.status === 403) {
          throw new Error("관리자 인증 만료");
        }
        return response.json();
      })
      .then((data) => {
        if (!data.success) throw new Error(data.message || "조회 실패");
        const next = normalizeResponse(data);
        setSettings(next);
        setSaved(next);
        setLegacySettings(
          ROLE_INFO.filter((role) => data.roles?.[role.key]?.legacySetting)
            .map((role) => role.name),
        );
      })
      .catch((fetchError) => setError(fetchError.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(saved),
    [saved, settings],
  );

  const effectiveModel = (roleKey) => {
    const roleModel = settings.roles[roleKey];
    return roleModel === "inherit" ? settings.defaultModel : roleModel;
  };

  const updateRole = (roleKey, model) => {
    setSettings((current) => ({
      ...current,
      roles: { ...current.roles, [roleKey]: model },
    }));
    setNotice("");
  };

  const useGeminiEverywhere = () => {
    setSettings({
      defaultModel: "gemini",
      roles: {
        classification: "inherit",
        factExtraction: "inherit",
        answer: "inherit",
      },
    });
    setNotice("");
  };

  const save = async () => {
    setSaving(true);
    setNotice("");
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/llm-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(settings),
      });
      if (response.status === 401 || response.status === 403) {
        throw new Error("관리자 인증 만료");
      }
      const data = await response.json();
      if (!data.success) throw new Error(data.message || "저장 실패");
      const next = normalizeResponse(data);
      setSettings(next);
      setSaved(next);
      setLegacySettings([]);
      setNotice("저장되었습니다 — 모든 LLM 경로에 60초 안에 반영됩니다.");
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={S.loading}>불러오는 중…</div>;

  return (
    <div style={S.page}>
      <h3 style={S.title}>AI 모델 설정</h3>
      <p style={S.intro}>
        기본 모델 하나를 모든 AI 역할이 함께 사용합니다. 특별한 이유가 있을 때만
        고급 설정에서 역할별 모델을 재정의하세요. 최종 세번 판정은 모델이 아니라
        결정트리가 수행합니다.
      </p>

      {error && (
        <div style={S.error}>
          {error}
          {String(error).includes("인증") && " — 관리자 로그인이 필요합니다."}
        </div>
      )}
      {legacySettings.length > 0 && (
        <div style={S.warning}>
          {legacySettings.join(", ")}의 기존 듀얼 설정은 지원 대상에서 제외되었습니다.
          저장하면 기본 모델 상속으로 정리됩니다.
        </div>
      )}

      <section style={S.section}>
        <div style={S.sectionHeader}>
          <div>
            <div style={S.sectionTitle}>통합 기본 모델</div>
            <div style={S.sectionDesc}>권장값은 Gemini 3.7 Flash입니다.</div>
          </div>
          <button type="button" onClick={useGeminiEverywhere} style={S.resetButton}>
            Gemini 3.7로 통일
          </button>
        </div>
        <div style={S.options}>
          {MODEL_OPTIONS.map((option) => (
            <label
              key={option.value}
              style={{
                ...S.option,
                ...(settings.defaultModel === option.value ? S.optionSelected : {}),
              }}
            >
              <input
                type="radio"
                name="default-llm-model"
                value={option.value}
                checked={settings.defaultModel === option.value}
                onChange={() => {
                  setSettings((current) => ({
                    ...current,
                    defaultModel: option.value,
                  }));
                  setNotice("");
                }}
                style={S.radio}
              />
              <span>
                <span style={S.optionName}>{option.name}</span>
                {saved.defaultModel === option.value && (
                  <span style={S.applied}>저장된 기본값</span>
                )}
                <span style={S.optionDesc}>{option.desc}</span>
              </span>
            </label>
          ))}
        </div>
      </section>

      <section style={S.section}>
        <div style={S.sectionTitle}>현재 역할별 적용</div>
        <div style={S.roleGrid}>
          {ROLE_INFO.map((role) => (
            <div key={role.key} style={S.roleCard}>
              <div style={S.roleName}>{role.name}</div>
              <div style={S.roleModel}>{modelName(effectiveModel(role.key))}</div>
              <div style={S.roleMode}>
                {settings.roles[role.key] === "inherit" ? "기본 모델 사용" : "개별 재정의"}
              </div>
              <div style={S.roleDesc}>{role.desc}</div>
            </div>
          ))}
          <div style={S.roleCard}>
            <div style={S.roleName}>이미지·스캔 인식</div>
            <div style={S.roleModel}>Gemini 3.7 Flash</div>
            <div style={S.roleMode}>멀티모달 고정</div>
            <div style={S.roleDesc}>이미지에서 추출한 사실은 사용자 확인 후 결정트리에 입력</div>
          </div>
        </div>
      </section>

      <section style={S.section}>
        <button
          type="button"
          onClick={() => setAdvanced((current) => !current)}
          style={S.advancedButton}
        >
          {advanced ? "고급 설정 닫기" : "역할별 고급 설정"}
        </button>
        {advanced && (
          <div style={S.advancedPanel}>
            {ROLE_INFO.map((role) => (
              <label key={role.key} style={S.selectRow}>
                <span>
                  <span style={S.selectLabel}>{role.name}</span>
                  <span style={S.selectDesc}>{role.desc}</span>
                </span>
                <select
                  value={settings.roles[role.key]}
                  onChange={(event) => updateRole(role.key, event.target.value)}
                  style={S.select}
                >
                  <option value="inherit">
                    기본 모델 사용 ({modelName(settings.defaultModel)})
                  </option>
                  {MODEL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.name}
                    </option>
                  ))}
                  {role.allowDual && (
                    <option value="dual">Gemini + Claude 교차검증</option>
                  )}
                </select>
              </label>
            ))}
            <div style={S.advancedHint}>
              듀얼 교차검증은 분류 후보 검토에만 허용합니다. 답변 작성과 사실추출에는
              이중 호출을 사용하지 않습니다.
            </div>
          </div>
        )}
      </section>

      <div style={S.footer}>
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          style={{
            ...S.saveButton,
            ...(!dirty ? S.saveButtonDisabled : {}),
          }}
        >
          {saving ? "저장 중…" : "설정 저장"}
        </button>
        {notice && <span style={S.notice}>{notice}</span>}
      </div>

      <div style={S.keyHint}>
        Claude·OpenAI 재정의는 서버에 해당 API 키가 구성된 경우에만 적용되며,
        미구성 시 Gemini로 안전하게 강등됩니다.
      </div>
    </div>
  );
};

const S = {
  loading: { padding: 24, fontSize: 13 },
  page: { padding: 24, maxWidth: 860 },
  title: { margin: "0 0 6px", fontSize: 18 },
  intro: { margin: "0 0 18px", fontSize: 13, color: "#5f6368", lineHeight: 1.7 },
  error: {
    padding: "9px 12px", background: "#fce8e6", color: "#a50e0e",
    borderRadius: 6, fontSize: 12, marginBottom: 12,
  },
  warning: {
    padding: "9px 12px", background: "#fff4e5", color: "#8a4b00",
    borderRadius: 6, fontSize: 12, marginBottom: 12,
  },
  section: {
    border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 14,
  },
  sectionHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
    marginBottom: 12,
  },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: "#202124" },
  sectionDesc: { fontSize: 12, color: "#6b7280", marginTop: 3 },
  resetButton: {
    border: "1px solid #1a73e8", background: "#fff", color: "#1a73e8",
    borderRadius: 6, padding: "7px 11px", fontSize: 12, fontWeight: 600, cursor: "pointer",
  },
  options: { display: "flex", flexDirection: "column", gap: 8 },
  option: {
    display: "flex", alignItems: "flex-start", gap: 10, padding: "11px 13px",
    border: "1px solid #e0e0e0", background: "#fff", borderRadius: 8, cursor: "pointer",
  },
  optionSelected: { borderColor: "#1a73e8", background: "#eff6ff" },
  radio: { marginTop: 3 },
  optionName: { fontSize: 13, fontWeight: 700 },
  optionDesc: { display: "block", fontSize: 12, color: "#666", marginTop: 2 },
  applied: { fontSize: 11, color: "#137333", marginLeft: 8 },
  roleGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 10, marginTop: 12,
  },
  roleCard: { padding: 12, borderRadius: 8, background: "#f8fafc", minHeight: 112 },
  roleName: { fontSize: 12, fontWeight: 700, color: "#374151" },
  roleModel: { fontSize: 14, fontWeight: 700, color: "#1a73e8", marginTop: 7 },
  roleMode: { fontSize: 11, color: "#137333", marginTop: 2 },
  roleDesc: { fontSize: 11, color: "#6b7280", lineHeight: 1.5, marginTop: 8 },
  advancedButton: {
    border: 0, padding: 0, background: "transparent", color: "#1a73e8",
    fontSize: 13, fontWeight: 700, cursor: "pointer",
  },
  advancedPanel: { display: "flex", flexDirection: "column", gap: 12, marginTop: 14 },
  selectRow: {
    display: "grid", gridTemplateColumns: "minmax(220px, 1fr) 280px",
    alignItems: "center", gap: 16,
  },
  selectLabel: { display: "block", fontSize: 13, fontWeight: 700 },
  selectDesc: { display: "block", fontSize: 11, color: "#6b7280", marginTop: 2 },
  select: {
    width: "100%", padding: "8px 9px", border: "1px solid #d1d5db",
    borderRadius: 6, background: "#fff", fontSize: 12,
  },
  advancedHint: { fontSize: 11, color: "#8a4b00", lineHeight: 1.5 },
  footer: { display: "flex", alignItems: "center", gap: 12 },
  saveButton: {
    padding: "9px 20px", border: 0, borderRadius: 6, background: "#1a73e8",
    color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
  },
  saveButtonDisabled: { background: "#e0e0e0", color: "#888", cursor: "default" },
  notice: { fontSize: 12, color: "#137333" },
  keyHint: { marginTop: 12, fontSize: 11, color: "#8a4b00", lineHeight: 1.6 },
};

export default AdminLlmModel;
